import { useEffect, useState } from 'react';

import { useHistory } from 'react-router';

import Spinner from 'src/app/components/Elements/Spinner';
import { SimpleFeatureLayoutPageContainer } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import { EscriptOnboardingAllDoctorsAllMeds } from 'src/app/components/Pages/EscriptOnboarding/steps/AllDoctorsAllMeds';
import { EscriptOnboardingAllergiesStep } from 'src/app/components/Pages/EscriptOnboarding/steps/Allergies';
import { EscriptOnboardingEnd } from 'src/app/components/Pages/EscriptOnboarding/steps/End';
import { EscriptOnboardingHowItWorksStep } from 'src/app/components/Pages/EscriptOnboarding/steps/HowItWorks';
import { EscriptOnboardingInsurance } from 'src/app/components/Pages/EscriptOnboarding/steps/Insurance';
import { EscriptOnboardingManageYourMeds } from 'src/app/components/Pages/EscriptOnboarding/steps/ManageYourMeds';
import { EscriptOnboardingOtherMeds } from 'src/app/components/Pages/EscriptOnboarding/steps/OtherMeds';
import { EscriptOnboardingOtherMedsYes } from 'src/app/components/Pages/EscriptOnboarding/steps/OtherMedsYes';
import { EscriptOnboardingSayCapsule } from 'src/app/components/Pages/EscriptOnboarding/steps/SayCapsule';
import { EscriptOnboardingWelcomeStep } from 'src/app/components/Pages/EscriptOnboarding/steps/Welcome';
import { EscriptOnboardingStepName } from 'src/app/components/Pages/EscriptOnboarding/types';
import { useOrderCount } from 'src/app/hooks/data/order';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { medications, newOnboardingStep } from 'src/app/router/paths';
import { ESCRIPT_ONBOARDING_FINISHED, ESCRIPT_ONBOARDING_STEP } from 'src/app/storage/localStorageKeys';

const useStepRedirect = ({ currentStepName }: { currentStepName: EscriptOnboardingStepName }) => {
  const [hasCheckedForPersistedStep, setHasCheckedForPersistedStep] = useState(false);
  const { replace } = useHistory();

  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));

  const { data: orderCount, error: orderCountError, isLoading } = useOrderCount();

  useEffect(() => {
    if (orderCountError) {
      replace(medications.route);
    }
  }, [orderCountError, replace]);

  useEffect(() => {
    if (!isLoading) {
      /** Don't show onboarding again if it's already been done and don't let user go back to it. */
      const hasFinishedOnboarding =
        user?.isLoginSet || !!window.localStorage.getItem(ESCRIPT_ONBOARDING_FINISHED) || (orderCount ?? 0) > 0;

      if (hasFinishedOnboarding) {
        window.localStorage.removeItem(ESCRIPT_ONBOARDING_STEP);
        replace(medications.route);
      } else {
        if (!hasCheckedForPersistedStep) {
          setHasCheckedForPersistedStep(true);

          const persistedStepName = window.localStorage.getItem(
            ESCRIPT_ONBOARDING_STEP
          ) as EscriptOnboardingStepName | null;

          if (persistedStepName) {
            replace(newOnboardingStep.toPath({ stepName: persistedStepName }));
          }
        }

        /** Otherwise, start the flow! */
        if (hasCheckedForPersistedStep && currentStepName === EscriptOnboardingStepName.redirect) {
          replace(newOnboardingStep.toPath({ stepName: EscriptOnboardingStepName.welcome }));
        }
      }
    }
  }, [currentStepName, replace, hasCheckedForPersistedStep, user, isLoading, orderCount]);

  return { hasCheckedForPersistedStep, isLoading };
};

export const LoadOnboardingStep = () => {
  const currentStepName = window.location.pathname.split('/').pop() as EscriptOnboardingStepName;

  const { hasCheckedForPersistedStep, isLoading } = useStepRedirect({ currentStepName });

  useEffect(() => {
    if (hasCheckedForPersistedStep) {
      window.localStorage.setItem(ESCRIPT_ONBOARDING_STEP, currentStepName);
    }
  }, [currentStepName, hasCheckedForPersistedStep]);

  if (isLoading) {
    return <Spinner isFullViewportSpinner />;
  }

  let renderedComponent = null;
  switch (currentStepName) {
    case EscriptOnboardingStepName.welcome:
      renderedComponent = <EscriptOnboardingWelcomeStep />;
      break;
    case EscriptOnboardingStepName.how_it_works:
      renderedComponent = <EscriptOnboardingHowItWorksStep />;
      break;
    case EscriptOnboardingStepName.insurance:
      renderedComponent = <EscriptOnboardingInsurance />;
      break;
    case EscriptOnboardingStepName.allergies:
      renderedComponent = <EscriptOnboardingAllergiesStep />;
      break;
    case EscriptOnboardingStepName.other_meds:
      renderedComponent = <EscriptOnboardingOtherMeds />;
      break;
    case EscriptOnboardingStepName.other_meds_yes:
      renderedComponent = <EscriptOnboardingOtherMedsYes />;
      break;
    case EscriptOnboardingStepName.manage_your_meds:
      renderedComponent = <EscriptOnboardingManageYourMeds />;
      break;
    case EscriptOnboardingStepName.any_doctor_any_med:
      renderedComponent = <EscriptOnboardingAllDoctorsAllMeds />;
      break;
    case EscriptOnboardingStepName.say_capsule:
      renderedComponent = <EscriptOnboardingSayCapsule />;
      break;
    case EscriptOnboardingStepName.end:
      renderedComponent = <EscriptOnboardingEnd />;
      break;
    default:
      renderedComponent = <Spinner isFullViewportSpinner />;
      break;
  }

  return <SimpleFeatureLayoutPageContainer key={currentStepName}>{renderedComponent}</SimpleFeatureLayoutPageContainer>;
};
